import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { rem, space } from '@gbi/ui';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import Radio, { RadioCircle, RadioIndicator } from '../InputFields/Radio';
import CheckboxGTT from '../InputFields/CheckboxGTT';
import tokens from '../../assets/styles/tokens';
import {
  GUTTER,
  ModalCloseIcon,
  WeightGrid,
  WeightOption,
  FullWidthApply,
} from './Common';

const DrawerTitle = styled.span`
  ${tokens.type.title[300]}
  color: ${tokens.color.text.primary};
`;

const AllWeightsSubtitle = styled.span`
  display: block;
  margin-top: ${rem(space[2])};
  ${tokens.type.body[200]}
  color: ${tokens.color.text.label};
`;

const AllWeightsTitle = styled.span`
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};
`;

// The frame's card: filled, no shadow, 8px/12px padding, a focus-colour border
// when selected, and the radio 10px from its text (off the 4px scale).
const AllWeightsRadio = styled(Radio)`
  &&& {
    padding: ${rem(space[8])} ${rem(space[12])};
    box-shadow: none;
    background-color: ${tokens.color.surface.sunken};
    ${({ selected }) =>
      selected && `border-color: ${tokens.color.border.focus};`}
  }

  ${RadioIndicator} {
    margin-right: ${rem(10)};
  }

  ${RadioCircle} {
    border-color: ${tokens.color.border.focus};

    &::after {
      background-color: ${tokens.color.border.focus};
    }
  }
`;

// A single "All Weights" (= no weight filter) radio card and a grid of
// weight checkboxes. The radio and the checkboxes are mutually exclusive:
// selecting the radio clears every checkbox; checking any weight clears the
// radio; unchecking the last weight re-selects the radio. "Apply" reports the
// selected weights (empty array = all) to the parent screen.
const SelectWeightDrawer = ({
  isOpen,
  onClose,
  availableWeights,
  selectedWeights,
  onApply,
}) => {
  const { t } = useTranslation();
  // Draft selection: edits stay local until "Apply". Empty = "All Weights".
  const [draftWeights, setDraftWeights] = useState(selectedWeights);

  // Re-seed the draft from the applied selection every time the drawer opens.
  useEffect(() => {
    if (isOpen) {
      setDraftWeights(selectedWeights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isAllSelected = draftWeights.length === 0;

  const selectAllWeights = () => setDraftWeights([]);

  const toggleWeight = (weight) => {
    setDraftWeights((prev) =>
      prev.includes(weight)
        ? prev.filter((w) => w !== weight)
        : [...prev, weight],
    );
  };

  const handleApply = () => {
    onApply(draftWeights);
    onClose();
  };

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <DrawerTitle>{t('gtt.triggerOrders.weight.drawerTitle')}</DrawerTitle>
      }
      closeIcon={<ModalCloseIcon data-cy="gtt-weight-close" />}
      headerPadding={`${GUTTER} ${GUTTER} 0`}
      contentPadding={`${rem(space[24])} ${GUTTER} ${rem(space[8])}`}
      footer={
        <FullWidthApply>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            type="button"
            onClick={handleApply}
            data-cy="gtt-weight-apply"
          >
            {t('common.apply')}
          </Button>
        </FullWidthApply>
      }
    >
      <AllWeightsRadio
        variant="RadioBox"
        name="gtt-weight-all"
        cssIndicator
        circleWidth="16px"
        circleHeight="16px"
        circleBorderWidth="2px"
        selected={isAllSelected}
        checked={isAllSelected}
        onClick={selectAllWeights}
        data-cy="gtt-weight-all"
      >
        <span>
          <AllWeightsTitle>
            {t('gtt.triggerOrders.weight.allWeights')}
          </AllWeightsTitle>
          <AllWeightsSubtitle>
            {t('gtt.triggerOrders.weight.allWeightsSubtitle')}
          </AllWeightsSubtitle>
        </span>
      </AllWeightsRadio>

      <WeightGrid>
        {availableWeights.map((weight) => {
          const checked = draftWeights.includes(weight);
          return (
            <WeightOption
              key={weight}
              onClick={() => toggleWeight(weight)}
              data-cy={`gtt-weight-${weight}`}
            >
              <CheckboxGTT
                checked={checked}
                ariaLabel={weight}
                name={`gtt-weight-${weight}`}
                thickBorder
                onClick={(e) => {
                  e.stopPropagation();
                  toggleWeight(weight);
                }}
              />
              {weight}
            </WeightOption>
          );
        })}
      </WeightGrid>
    </BottomDrawer>
  );
};

SelectWeightDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  availableWeights: PropTypes.arrayOf(PropTypes.string),
  selectedWeights: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func,
};

SelectWeightDrawer.defaultProps = {
  availableWeights: [],
  selectedWeights: [],
};

export default SelectWeightDrawer;
